import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { isApiErrorMessage } from 'src/api/utils/is-api-error-message';
import { ApiError } from 'src/types/interfaces/api-error';
import { delayedCallbackExecution } from 'src/utils/delayed-callback-execution';
import { employeesGroupsApiErrorsTranslations } from '../../constants/api-errors-translations';
import { createEmployeesGroup } from '../lib/employees-groups';
import { employeesGroupsKeys } from '../query-keys/employees-groups';
import { ApiErrorMessage } from '../types/api-error-message';

export const useCreateEmployeesGroup = (onSuccess?: () => void) => {
  const { t } = useTranslation(['employeesGroups', 'common']);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      name,
      employeesTokens,
    }: {
      name: string;
      employeesTokens: string[];
    }) => createEmployeesGroup(name, employeesTokens),
    onSuccess: () => {
      delayedCallbackExecution(1000, () => {
        queryClient.invalidateQueries({
          queryKey: employeesGroupsKeys.lists(),
        });
      });
      toast.success(
        t('employeesGroups:common.createEmployeesGroupDialog.successMessage'),
      );
      onSuccess?.();
    },
    onError: (error: ApiError) => {
      const errorMessage = error?.response?.data?.message;

      if (
        isApiErrorMessage<ApiErrorMessage>(
          errorMessage,
          employeesGroupsApiErrorsTranslations,
        )
      ) {
        toast.error(t(employeesGroupsApiErrorsTranslations[errorMessage]));

        return;
      }

      toast.error(errorMessage || t('common:errorMessages.somethingWentWrong'));
    },
  });
};
